import { useRef, useState } from 'react';
import type { PlaceOption } from './placeSearchData';

const SERIF = "'Noto Serif TC', serif";

/** 在文本中高亮命中的关键词片段 */
function HighlightText({ text, query, color }: { text: string; query: string; color: string }) {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ color }}>{text.slice(idx, idx + q.length)}</span>
      {text.slice(idx + q.length)}
    </>
  );
}

interface BirthPlaceSearchBarProps {
  value: string;
  placeholder: string;
  cancelLabel: string;
  accentColor: string;
  onChange: (value: string) => void;
  onCancel: () => void;
}

/** 地名搜索输入框：带搜索图标、清空按钮与「取消」 */
export function BirthPlaceSearchBar({
  value,
  placeholder,
  cancelLabel,
  accentColor,
  onChange,
  onCancel,
}: BirthPlaceSearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const showCancel = focused || value.length > 0;

  return (
    <div className="flex items-center gap-2">
      <div
        className="flex items-center gap-2 flex-1 min-w-0 rounded-full px-3"
        style={{ background: 'var(--accent-primary-10)', border: '1px solid var(--border-color)', height: 38 }}
      >
        <i className="ri-search-line text-sm flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
        <input
          ref={inputRef}
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          className="flex-1 min-w-0 bg-transparent outline-none text-sm"
          style={{ color: 'var(--text-primary)', fontFamily: SERIF }}
        />
        {value.length > 0 && (
          <button
            type="button"
            onClick={() => {
              onChange('');
              inputRef.current?.focus();
            }}
            className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 cursor-pointer"
            style={{ background: 'var(--text-primary-20)' }}
          >
            <i className="ri-close-line text-xs" style={{ color: 'var(--text-secondary)' }} />
          </button>
        )}
      </div>
      {showCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="text-sm flex-shrink-0 whitespace-nowrap cursor-pointer"
          style={{ color: accentColor, fontFamily: SERIF }}
        >
          {cancelLabel}
        </button>
      )}
    </div>
  );
}

interface BirthPlaceSuggestListProps {
  options: PlaceOption[];
  query: string;
  accentColor: string;
  emptyLabel: string;
  onPick: (value: string) => void;
}

/** 地名联想结果列表 */
export function BirthPlaceSuggestList({
  options,
  query,
  accentColor,
  emptyLabel,
  onPick,
}: BirthPlaceSuggestListProps) {
  if (options.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-2">
        <i className="ri-map-pin-line text-2xl" style={{ color: 'var(--text-tertiary)' }} />
        <span className="text-xs" style={{ color: 'var(--text-tertiary)', fontFamily: SERIF }}>
          {emptyLabel}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onPick(opt.value)}
          className="flex items-center gap-2 text-left px-3 py-2.5 rounded-lg transition-colors hover:bg-[var(--accent-primary-10)] cursor-pointer"
        >
          <i className="ri-map-pin-line text-sm flex-shrink-0" style={{ color: accentColor }} />
          <span
            className="text-sm truncate"
            style={{ color: 'var(--text-primary)', fontFamily: SERIF }}
          >
            {opt.segments.map((seg, i) => (
              <span key={`${seg}-${i}`}>
                {i > 0 && <span style={{ color: 'var(--text-tertiary)' }}>-</span>}
                <HighlightText text={seg} query={query} color={accentColor} />
              </span>
            ))}
          </span>
        </button>
      ))}
    </div>
  );
}